import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApi } from '../../shared/hooks/useApi'
import {
  formatDate,
  formatDateTime,
  getNoticeTypeLabel,
  formatCurrency,
} from '../../shared/utils/formatters'
import {
  ArrowLeft,
  Calendar,
  Building,
  MapPin,
  Tag,
  FileText,
  Users,
  Layers,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  AlertTriangle,
  CircleDollarSign,
  DollarSign,
  Sparkles,
  Loader2,
  RefreshCw,
  CheckCircle,
} from 'lucide-react'
import { differenceInDays, differenceInHours, parseISO, isPast } from 'date-fns'
import TenderTimeline from './components/TenderTimeline'
import ReactMarkdown from 'react-markdown'

interface TenderDetail {
  id: string
  notice_number?: string
  notice_type: string
  organization_name: string
  order_object?: string
  publication_date: string
  submitting_offers_date?: string
  client_type?: string
  client_type_label?: string
  order_type?: string
  order_type_label?: string
  tender_type?: string
  tender_type_label?: string
  is_tender_amount_below_eu: boolean
  procedure_result?: string
  procedure_result_label?: string
  tender_id?: string
  bzp_number?: string
  cpv_code?: string
  total_contract_value?: number
  parsed_planned_contract_value?: number
  is_multipart: boolean
  parts?: TenderPart[]
  contractors?: Contractor[]
  html_body?: string
  created_at: string
  updated_at: string
}

interface TenderPart {
  id: string
  part_number: number
  description?: string
  estimated_value?: number
  contract_value?: number
  contractor_name?: string
  offers_received?: number
  offers_rejected?: number
  currency: string
}

interface Contractor {
  contractor_name?: string
  contractor_city?: string
  contractor_province?: string
  contractor_country?: string
  contractor_national_id?: string
}

interface TotalContractValueProps {
  value?: number
  procedureResult?: string
}

function TotalContractValue({ value, procedureResult }: TotalContractValueProps) {
  let displayValue: string
  let subText = 'Całkowita wartość umowy'

  if (value) {
    displayValue = formatCurrency(value, 'PLN')
  } else if (procedureResult === 'uniewaznienie') {
    displayValue = 'Unieważnienie'
    subText = 'Wynik postępowania'
  } else if (procedureResult === 'nieRozstrzygnieto') {
    displayValue = 'Nie rozstrzygnięto'
    subText = 'Wynik postępowania'
  } else {
    displayValue = 'Brak danych o wartości'
  }

  const theme = {
    bg: 'bg-surface-raised',
    border: 'border-brand',
    iconBg: 'bg-brand-bg',
    iconColor: 'text-brand',
    textColor: 'text-brand',
    subText: 'text-brand',
  }

  return (
    <div
      className={`flex items-center p-4 rounded-lg border ${theme.bg} ${theme.border} col-span-1 md:col-span-1`}
    >
      <div className={`${theme.iconBg} p-2 rounded-lg mr-4 flex items-center justify-center`}>
        <CircleDollarSign className={`h-6 w-6 ${theme.iconColor}`} />
      </div>
      <div className="flex-1">
        <div className={`text-xs ${theme.subText} font-semibold uppercase tracking-wide mb-1`}>
          {subText}
        </div>
        <div className={`text-xl font-bold ${theme.textColor} leading-none`}>{displayValue}</div>
      </div>
    </div>
  )
}

interface PlannedContractValueProps {
  value?: number
}

function PlannedContractValue({ value }: PlannedContractValueProps) {
  let displayValue: string
  const subText = 'Planowana wartość umowy'

  if (value) {
    displayValue = formatCurrency(value, 'PLN')
  } else {
    displayValue = 'Brak danych o wartości'
  }

  const theme = {
    bg: 'bg-surface-raised',
    border: 'border-success/20',
    iconBg: 'bg-success/5',
    iconColor: 'text-success',
    textColor: 'text-success',
    subText: 'text-success',
  }

  return (
    <div
      className={`flex items-center p-4 rounded-lg border ${theme.bg} ${theme.border} col-span-1 md:col-span-1`}
    >
      <div className={`${theme.iconBg} p-2 rounded-lg mr-4 flex items-center justify-center`}>
        <DollarSign className={`h-6 w-6 ${theme.iconColor}`} />
      </div>
      <div className="flex-1">
        <div className={`text-xs ${theme.subText} font-semibold uppercase tracking-wide mb-1`}>
          {subText}
        </div>
        <div className={`text-xl font-bold ${theme.textColor} leading-none`}>{displayValue}</div>
      </div>
    </div>
  )
}

interface DeadlineTimerProps {
  date?: string
}

function DeadlineTimer({ date }: DeadlineTimerProps) {
  if (!date) return null

  const targetDate = typeof date === 'string' ? parseISO(date) : date
  const now = new Date()
  const isExpired = isPast(targetDate)

  if (isExpired) {
    return (
      <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border col-span-1 md:col-span-2">
        <div className="bg-surface-inset p-2 rounded-lg mr-4 flex items-center justify-center">
          <Calendar className="h-6 w-6 text-ink-muted" />
        </div>
        <div>
          <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
            Pozostały czas
          </div>
          <div className="text-base font-bold text-ink-muted">
            Termin minął ({formatDateTime(date)})
          </div>
        </div>
      </div>
    )
  }

  const days = differenceInDays(targetDate, now)
  const businessDays = differenceInDays(targetDate, now) // Simplified
  const hours = differenceInHours(targetDate, now) % 24

  let theme = {
    bg: 'bg-surface-raised',
    border: 'border-success/20',
    iconBg: 'bg-success/5',
    iconColor: 'text-success',
    textColor: 'text-success',
    subText: 'text-success',
  }

  if (businessDays < 3) {
    theme = {
      bg: 'bg-surface-raised',
      border: 'border-danger/20',
      iconBg: 'bg-danger/5',
      iconColor: 'text-danger',
      textColor: 'text-danger',
      subText: 'text-danger',
    }
  } else if (businessDays < 5) {
    theme = {
      bg: 'bg-surface-raised',
      border: 'border-warning/20',
      iconBg: 'bg-warning/5',
      iconColor: 'text-warning',
      textColor: 'text-warning',
      subText: 'text-warning',
    }
  } else if (businessDays < 10) {
    theme = {
      bg: 'bg-surface-raised',
      border: 'border-warning/20',
      iconBg: 'bg-warning/5',
      iconColor: 'text-warning',
      textColor: 'text-warning',
      subText: 'text-warning',
    }
  }

  return (
    <div
      className={`flex items-center p-4 rounded-lg border ${theme.bg} ${theme.border} col-span-1 md:col-span-2`}
    >
      <div className={`${theme.iconBg} p-2 rounded-lg mr-4 flex items-center justify-center`}>
        {businessDays < 3 ? (
          <AlertTriangle className={`h-6 w-6 ${theme.iconColor}`} />
        ) : (
          <Clock className={`h-6 w-6 ${theme.iconColor}`} />
        )}
      </div>
      <div className="flex-1">
        <div className={`text-xs ${theme.subText} font-semibold uppercase tracking-wide mb-1`}>
          Pozostało czasu
        </div>
        <div className={`text-xl font-bold ${theme.textColor} leading-none`}>
          {days} dni {hours} godz.
        </div>
      </div>
    </div>
  )
}

interface DetailSectionProps {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}

function DetailSection({ title, icon: Icon, children }: DetailSectionProps) {
  return (
    <div className="bg-surface-raised border border-border rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-medium text-ink flex items-center">
          <Icon className="h-5 w-5 mr-2 text-ink-muted" />
          {title}
        </h3>
      </div>
      <div className="p-6 space-y-0">{children}</div>
    </div>
  )
}

interface DetailRowProps {
  label: string
  value?: string | boolean
  className?: string
}

function DetailRow({ label, value, className = '' }: DetailRowProps) {
  if (!value) return null
  return (
    <div className={`flex justify-between py-1 ${className}`}>
      <span className="text-sm font-medium text-ink-secondary">{label}:</span>
      <span className="text-sm text-ink text-right ml-4">{value}</span>
    </div>
  )
}

// PredictionSection component for AI predictions
interface PredictionStep {
  step: string
  content: string
}

interface PredictionData {
  prediction_markdown: string
  created_at: string
  model_used: string
  analysis_steps?: PredictionStep[]
}

interface PredictionSectionProps {
  noticeId: string
}

function PredictionSection({ noticeId }: PredictionSectionProps) {
  const { generatePrediction, getPrediction } = useApi()
  const [prediction, setPrediction] = useState<PredictionData | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [currentSteps, setCurrentSteps] = useState<PredictionStep[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [progressPercent, setProgressPercent] = useState(0)
  const controllerRef = useRef<AbortController | null>(null)

  // Load existing prediction on mount
  useEffect(() => {
    const loadPrediction = async () => {
      try {
        const data = await getPrediction(noticeId)
        setPrediction(data)
      } catch (err: any) {
        // 404 is expected when no prediction exists
        if (err?.response?.status !== 404) {
          console.error('Error loading prediction:', err)
        }
      } finally {
        setIsLoading(false)
      }
    }
    loadPrediction()
  }, [noticeId, getPrediction])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
    }
  }, [])

  const handleGenerate = () => {
    setIsGenerating(true)
    setCurrentSteps([])
    setProgressPercent(0)
    setError(null)

    controllerRef.current = generatePrediction(
      noticeId,
      // onStep
      (stepData: PredictionStep) => {
        setCurrentSteps((prev) => {
          const updatedSteps = [...prev, stepData]
          // Calculate progress based on step completion (4 steps total)
          const progress = Math.min((updatedSteps.length / 4) * 100, 90)
          setProgressPercent(progress)
          return updatedSteps
        })
      },
      // onResult
      (resultData: PredictionData) => {
        setPrediction(resultData)
        setIsGenerating(false)
        setCurrentSteps([])
        setProgressPercent(100)
      },
      // onError
      (errorMsg: string) => {
        setError(errorMsg)
        setIsGenerating(false)
        setProgressPercent(0)
      },
    )
  }

  const stepLabels: Record<string, string> = {
    planning: '📋 Planowanie analizy',
    context_gathering: '🔍 Zbieranie kontekstu z bazy danych',
    analysis: '📊 Analiza danych i kontekstu',
    prediction: '🎯 Generowanie predykcji zwycięzców',
  }

  if (isLoading) {
    return (
      <div className="bg-surface-raised border border-border rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-medium text-ink flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-ai" />
            Predykcja AI
          </h3>
        </div>
        <div className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-ink-faint" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-surface-raised border border-border rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h3 className="text-lg font-medium text-ink flex items-center">
          <Sparkles className="h-5 w-5 mr-2 text-ai" />
          Predykcja AI
        </h3>
        {!isGenerating && (
          <button
            onClick={handleGenerate}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-ai rounded-md hover:bg-ai transition-colors"
          >
            {prediction ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Wygeneruj ponownie
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generuj predykcję
              </>
            )}
          </button>
        )}
      </div>

      <div className="p-6">
        {/* Error display */}
        {error && (
          <div className="bg-danger/5 border border-danger/20 rounded-md p-4 mb-4">
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}

        {/* Generation in progress */}
        {isGenerating && (
          <div className="space-y-6">
            {/* Progress Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm font-medium text-ink-secondary">
                <Loader2 className="h-5 w-5 animate-spin mr-2 text-ai" />
                Generowanie predykcji...
              </div>
              <div className="text-sm text-ink-muted">{Math.round(progressPercent)}%</div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-surface-inset rounded-full h-2">
              <div
                className="bg-ai h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>

            {/* Steps */}
            <div className="space-y-3">
              {currentSteps.map((step, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-ai/5 to-ai/10 rounded-lg p-4 border border-ai/10 animate-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-6 h-6 bg-ai rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-ink mb-1">
                        {stepLabels[step.step] || step.step}
                      </div>
                      <div className="text-xs text-ink-secondary leading-relaxed">{step.content}</div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Next step indicator */}
              {currentSteps.length < 4 && (
                <div className="bg-surface rounded-lg p-4 border border-border border-dashed">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-surface-inset rounded-full flex items-center justify-center mr-3">
                      <Loader2 className="h-3 w-3 animate-spin text-ink-muted" />
                    </div>
                    <div className="text-sm text-ink-muted">
                      {currentSteps.length === 0 && 'Przygotowywanie analizy...'}
                      {currentSteps.length === 1 && 'Zbieranie kontekstu...'}
                      {currentSteps.length === 2 && 'Analizowanie danych...'}
                      {currentSteps.length === 3 && 'Generowanie predykcji...'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Saved prediction display */}
        {!isGenerating && prediction && (
          <div className="space-y-4">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{prediction.prediction_markdown}</ReactMarkdown>
            </div>
            <div className="text-xs text-ink-muted pt-4 border-t border-border-subtle">
              <div>Model: {prediction.model_used}</div>
              <div>Wygenerowano: {formatDateTime(prediction.created_at)}</div>
            </div>
          </div>
        )}

        {/* No prediction yet */}
        {!isGenerating && !prediction && !error && (
          <div className="text-center py-8 text-ink-muted">
            <Sparkles className="h-12 w-12 mx-auto mb-3 text-ink-faint" />
            <p className="text-sm">
              Kliknij &ldquo;Generuj predykcję&rdquo; aby wygenerować analizę AI dla tego ogłoszenia.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

const TenderDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getNoticeDetail, loading, error } = useApi()
  const [notice, setNotice] = useState<TenderDetail | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TenderPart
    direction: 'asc' | 'desc'
  }>({
    key: 'part_number',
    direction: 'asc',
  })

  useEffect(() => {
    const loadNoticeDetail = async () => {
      try {
        const data = await getNoticeDetail(id!)
        setNotice(data)
      } catch (err) {
        console.error('Failed to load notice detail:', err)
      }
    }

    if (id) {
      loadNoticeDetail()
    }
  }, [id, getNoticeDetail])

  const sortedParts = useMemo(() => {
    if (!notice || !notice.parts) return []

    return [...notice.parts].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0
      const bValue = b[sortConfig.key] ?? 0

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [notice, sortConfig])

  const requestSort = (key: keyof TenderPart) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const SortIcon = ({ columnKey }: { columnKey: keyof TenderPart }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-4 w-4 ml-1 text-ink-faint" />
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1 text-brand" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1 text-brand" />
    )
  }

  const SortableHeader = ({
    label,
    columnKey,
    className = '',
  }: {
    label: string
    columnKey: keyof TenderPart
    className?: string
  }) => (
    <th
      scope="col"
      className={`px-3 py-3 text-left text-xs font-bold text-ink-secondary uppercase tracking-wider cursor-pointer hover:bg-surface-inset transition-colors ${className}`}
      onClick={() => requestSort(columnKey)}
    >
      <div className="flex items-center">
        {label}
        <SortIcon columnKey={columnKey} />
      </div>
    </th>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/tenders')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Powrót do listy
        </button>
        <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
          <p className="text-danger">Błąd podczas ładowania szczegółów ogłoszenia: {error}</p>
        </div>
      </div>
    )
  }

  if (!notice) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/tenders')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-ink-secondary bg-surface-raised border border-border rounded-md hover:bg-surface-hover"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Powrót do listy
        </button>
        <div className="text-sm text-ink-muted">ID: {notice.id}</div>
      </div>

      {/* Title & Key Details */}
      <div className="bg-surface-raised border border-border rounded-lg p-6">
        {/* Top Bar: Type & ID */}
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-mono text-ink-secondary bg-surface px-3 py-1 rounded border border-border-subtle">
            {getNoticeTypeLabel(notice.notice_type)}
          </span>
          <span className="text-sm font-mono text-ink-secondary bg-surface px-3 py-1 rounded border border-border">
            Numer ogłoszenia: {notice.notice_number || `#${notice.id}`}
          </span>
        </div>

        {/* Organization */}
        <div className="mb-4">
          <div className="text-xs text-ink-muted uppercase tracking-wider font-semibold mb-1">
            Zamawiający
          </div>
          <button
            onClick={() => {
              navigate(`/organizations/${encodeURIComponent(notice.organization_name)}`)
            }}
            className="text-xl md:text-2xl font-bold text-brand hover:text-brand hover:underline leading-tight text-left"
          >
            {notice.organization_name}
          </button>
        </div>

        {/* Order Object */}
        <div className="mb-6">
          <div className="text-xs text-ink-muted uppercase tracking-wider font-semibold mb-1">
            Przedmiot zamówienia
          </div>
          <p className="text-lg text-ink leading-relaxed font-medium">{notice.order_object}</p>
        </div>

        {/* Dates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* 1. Publication Date */}
          <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border md:col-span-1">
            <div className="bg-brand-bg p-2 rounded-lg mr-4 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-brand" />
            </div>
            <div>
              <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
                Data publikacji
              </div>
              <div className="text-base font-bold text-ink">
                {formatDate(notice.publication_date)}
              </div>
            </div>
          </div>

          {/* 2. Deadline Date (or Status) */}
          {notice.notice_type === 'ContractNotice' && notice.submitting_offers_date ? (
            <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border md:col-span-1">
              <div className="bg-warning/5 p-2 rounded-lg mr-4 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-warning" />
              </div>
              <div>
                <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
                  Termin składania
                </div>
                <div className="text-base font-bold text-ink">
                  {formatDateTime(notice.submitting_offers_date)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center p-4 bg-surface-raised rounded-lg border border-border md:col-span-1">
              <div className="bg-ai/5 p-2 rounded-lg mr-4 flex items-center justify-center">
                <FileText className="h-6 w-6 text-ai" />
              </div>
              <div>
                <div className="text-xs text-ink-muted font-semibold uppercase tracking-wide mb-1">
                  Status
                </div>
                <div className="text-base font-bold text-ink">
                  {notice.notice_type === 'TenderResultNotice' ? 'Rozstrzygnięcie' : 'Informacyjne'}
                </div>
              </div>
            </div>
          )}

          {/* 3. Dynamic Timer (Double Width) */}
          {notice.notice_type === 'ContractNotice' && notice.submitting_offers_date && (
            <DeadlineTimer date={notice.submitting_offers_date} />
          )}

          {/* 3. Planned Contract Value - For TenderResultNotice */}
          {notice.notice_type === 'TenderResultNotice' && (
            <PlannedContractValue value={notice.parsed_planned_contract_value} />
          )}

          {/* 4. Total Contract Value - For TenderResultNotice */}
          {notice.notice_type === 'TenderResultNotice' && (
            <TotalContractValue
              value={notice.total_contract_value}
              procedureResult={notice.procedure_result}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <DetailSection title="Informacje podstawowe" icon={FileText}>
          <DetailRow label="Numer ogłoszenia" value={notice.notice_number} />
          <DetailRow label="Numer BZP" value={notice.bzp_number} />
          <DetailRow label="Typ klienta" value={notice.client_type_label || notice.client_type} />
          <DetailRow label="Typ zamówienia" value={notice.order_type_label || notice.order_type} />
          <DetailRow
            label="Typ postępowania"
            value={notice.tender_type_label || notice.tender_type}
          />
          <DetailRow
            label="Poniżej progu UE"
            value={notice.is_tender_amount_below_eu ? 'Tak' : 'Nie'}
          />
          <DetailRow
            label="Wynik postępowania"
            value={notice.procedure_result_label || notice.procedure_result}
          />
          <DetailRow label="ID postępowania" value={notice.tender_id} />
        </DetailSection>

        {/* CPV Codes */}
        <DetailSection title="Kody CPV" icon={Tag}>
          {notice.cpv_code ? (
            <ul className="list-disc pl-5 space-y-1 text-ink text-sm">
              {notice.cpv_code.split(',').map((code, index) => (
                <li key={index}>{code.trim()}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink">-</p>
          )}
        </DetailSection>
      </div>

      {/* Tender Timeline */}
      <TenderTimeline
        noticeId={id!}
        onNoticeClick={(relatedNoticeId) => {
          navigate(`/tenders/${relatedNoticeId}`)
        }}
      />

      {/* AI Prediction - only for ContractNotice */}
      {notice.notice_type === 'ContractNotice' && <PredictionSection noticeId={id!} />}

      {/* Multipart Tender Parts */}
      {notice.is_multipart && notice.parts && notice.parts.length > 0 && (
        <DetailSection title={`Części zamówienia (${notice.parts.length})`} icon={Layers}>
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-inset">
                <tr>
                  <SortableHeader label="Nr" columnKey="part_number" className="w-16" />
                  <SortableHeader
                    label="Opis"
                    columnKey="description"
                    className="w-2/5 min-w-[300px]"
                  />
                  <SortableHeader label="Szacowana wartość" columnKey="estimated_value" />
                  <SortableHeader label="Wartość umowy" columnKey="contract_value" />
                  <SortableHeader label="Wykonawca" columnKey="contractor_name" />
                  <SortableHeader label="Oferty" columnKey="offers_received" />
                </tr>
              </thead>
              <tbody className="bg-surface-raised divide-y divide-border">
                {sortedParts.map((part) => (
                  <tr key={part.id} className="hover:bg-brand-bg transition-colors">
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-ink align-top border-r border-border-subtle last:border-r-0">
                      {part.part_number}
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-secondary align-top border-r border-border-subtle last:border-r-0">
                      <div className="whitespace-pre-wrap" title={part.description}>
                        {part.description || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-ink-secondary align-top border-r border-border-subtle last:border-r-0 font-mono">
                      {part.estimated_value ? (
                        <span>{formatCurrency(part.estimated_value, part.currency)}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-ink-secondary align-top border-r border-border-subtle last:border-r-0 font-mono">
                      {part.contract_value ? (
                        <span className="font-medium text-ink">
                          {formatCurrency(part.contract_value, part.currency)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-secondary align-top border-r border-border-subtle last:border-r-0">
                      {part.contractor_name || '-'}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-ink-secondary align-top">
                      <div className="flex flex-col">
                        <span title="Otrzymane">{part.offers_received ?? '-'}</span>
                        {part.offers_rejected && part.offers_rejected > 0 && (
                          <span className="text-xs text-danger" title="Odrzucone">
                            (odrz. {part.offers_rejected})
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DetailSection>
      )}

      {/* Contractors - Only shown if NOT multipart to avoid duplication, or if no parts data */}
      {(!notice.is_multipart || !notice.parts || notice.parts.length === 0) &&
        notice.contractors &&
        notice.contractors.length > 0 && (
          <DetailSection title="Wykonawcy" icon={Users}>
            <div className="space-y-4">
              {notice.contractors.map((contractor, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <h4 className="font-medium text-ink mb-2">
                    {contractor.contractor_name || 'Nieznana nazwa'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-ink-secondary">
                    <div>Miasto: {contractor.contractor_city || '-'}</div>
                    <div>Województwo: {contractor.contractor_province || '-'}</div>
                    <div>Kraj: {contractor.contractor_country || '-'}</div>
                    <div>NIP: {contractor.contractor_national_id || '-'}</div>
                  </div>
                </div>
              ))}
            </div>
          </DetailSection>
        )}

      {/* HTML Content */}
      {notice.html_body && (
        <DetailSection title="Treść ogłoszenia" icon={FileText}>
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: notice.html_body }}
          />
        </DetailSection>
      )}

      {/* Timestamps */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="text-xs text-ink-muted space-y-1">
          <div>Utworzone: {formatDateTime(notice.created_at)}</div>
          <div>Ostatnia aktualizacja: {formatDateTime(notice.updated_at)}</div>
        </div>
      </div>
    </div>
  )
}

export default TenderDetailPage
